import { useState, type ReactNode } from "react";
import type { AcceptanceResult, LoopDetail, LoopIteration, ProgressEntry, ShipAttempt, Task } from "../types";
import { fmtUsd } from "./usageChart";
import { stageSpans } from "./shipStages";
import { RunTrace } from "./RunTrace";
import {
	ITERATION_STATUS_LABEL,
	ITERATION_TONE,
	OUTCOME_STATUS_LABEL,
	OUTCOME_TONE,
	SHIP_STAGE_LABEL,
	SHIP_STATUS_LABEL,
	SHIP_TONE,
	clock,
	loopNow,
	runName,
	splitDiff,
	timeline,
	took,
	type TimelineEvent,
	type Tone,
} from "./loopGlance";

/** A page one step down from a loop: an iteration's or a ship attempt's details, an iteration's changes, the loop's information or its task list. */
export type SubPage = { page: "iteration"; id: string } | { page: "ship"; id: string } | { page: "changes"; id: string } | { page: "info" } | { page: "tasks" };

/** What the person can do from a loop's pages. */
export interface LoopActions {
	onOpen: (page: SubPage) => void;
	onAbort: () => void;
	onResume: () => void;
	onReview: (verdict: { approve: boolean; note?: string }) => void;
	onDelete: () => void;
	/** Goes to the task page, where the deployment is approved. */
	onDeploy: () => void;
	onChat: (iteration: LoopIteration) => void;
}

/** A ship attempt's number, counted from the loop's first. */
function shipNumber(loop: LoopDetail, id: string): number {
	return [...loop.ships].sort((a, b) => a.createdAt - b.createdAt).findIndex((s) => s.id === id) + 1;
}

export function pageTitle(loop: LoopDetail, page: SubPage): string {
	switch (page.page) {
		case "iteration": {
			const it = loop.iterations.find((i) => i.id === page.id);
			return it ? runName(loop.oneShot, it.index) : "";
		}
		case "ship":
			return `发布 #${shipNumber(loop, page.id)}`;
		case "changes":
			return "改动";
		case "info":
			return "信息与花费";
		case "tasks":
			return "任务清单";
	}
}

/** A loop, or the page under it that `page` names. */
export function LoopPage({ loop, page, now, ...actions }: { loop: LoopDetail; page?: SubPage; now: number } & LoopActions) {
	if (!page) return <LoopView key={loop.id} loop={loop} now={now} {...actions} />;
	switch (page.page) {
		case "iteration": {
			const it = loop.iterations.find((i) => i.id === page.id);
			return it ? <IterationPage loop={loop} it={it} now={now} onOpen={actions.onOpen} onChat={actions.onChat} /> : <Gone />;
		}
		case "ship": {
			const ship = loop.ships.find((s) => s.id === page.id);
			return ship ? <ShipPage ship={ship} number={shipNumber(loop, ship.id)} now={now} onDeploy={actions.onDeploy} /> : <Gone />;
		}
		case "changes": {
			const it = loop.iterations.find((i) => i.id === page.id);
			return it ? <ChangesPage loop={loop} it={it} /> : <Gone />;
		}
		case "info":
			return <InfoPage loop={loop} />;
		case "tasks":
			return <TasksPage loop={loop} />;
	}
}

const Gone = () => <p className="hint">此项已不存在</p>;

function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
	return <span className={`pill tone-${tone}`}>{children}</span>;
}

/** Token counts read as sizes: 24k, 1.2M. */
function fmtTokens(n: number): string {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1000) return `${Math.round(n / 1000)}k`;
	return String(n);
}

/** Iterations' spend in one phrase. */
function usageLine(items: Array<LoopIteration["usage"]>): string | null {
	const used = items.filter((u): u is NonNullable<typeof u> => Boolean(u));
	if (used.length === 0) return null;
	const inTokens = used.reduce((n, u) => n + u.input + u.cacheRead + u.cacheWrite, 0);
	const out = used.reduce((n, u) => n + u.output, 0);
	return `${fmtTokens(inTokens)} in · ${fmtTokens(out)} out · ${usageCost(used)}`;
}

function usageCost(used:NonNullable<LoopIteration["usage"]>[]):string {
	const known=used.filter(u=>u.costStatus !== "unknown" || u.cost > 0);
	const cost=known.reduce((n,u)=>n+u.cost,0);
	if(used.some(u=>u.costStatus === "unknown")) return known.length ? `折合 ${fmtUsd(cost)}＋未定价` : "未定价";
	return `折合 ${fmtUsd(cost)}${used.some(u=>u.costStatus === "estimated") ? "（估算）" : ""}`;
}

const ACTIVE = ["running", "awaiting_review", "waiting_quota"];

/** A loop: where it stands now, what happened to it newest first, and the rows to its information and task list. */
function LoopView({ loop, now, ...a }: { loop: LoopDetail; now: number } & LoopActions) {
	// The server refuses to delete a loop while it runs or its ship attempt does.
	const deletable = !ACTIVE.includes(loop.status) && !loop.ships.some((s) => s.status === "running");
	const tasks = loop.ledger?.tasks ?? [];
	const used = loop.iterations.filter((it) => it.usage);
	const shown = loopNow(loop, now).failed;
	return (
		<div className="loop-view loop-page">
			<header className="loop-head">
				<h3>{loop.title}</h3>
				<p className="loop-meta">{[loop.profile, loop.oneShot ? "one-shot" : null, loop.change ? `改动 ${loop.change}` : null].filter(Boolean).join(" · ")}</p>
			</header>
			<NowCard key={`${loop.id}:${loop.status}`} loop={loop} now={now} {...a} />
			<Timeline loop={loop} now={now} shown={shown} onOpen={a.onOpen} />
			<div className="loop-links">
				{tasks.length > 0 && (
					<button className="loop-link" onClick={() => a.onOpen({ page: "tasks" })}>
						<span>任务清单</span>
						<span className="loop-link-value">
							{tasks.filter((t) => t.passes).length}/{tasks.length} 完成
						</span>
						<span className="loop-chev">›</span>
					</button>
				)}
				<button className="loop-link" onClick={() => a.onOpen({ page: "info" })}>
					<span>信息与花费</span>
					<span className="loop-link-value">{used.length > 0 ? usageCost(used.map(it=>it.usage!)) : ""}</span>
					<span className="loop-chev">›</span>
				</button>
			</div>
			{deletable && (
				<button className="ghost loop-delete" onClick={a.onDelete}>
					删除此 loop
				</button>
			)}
		</div>
	);
}

/** Where the loop stands, with the buttons for what it waits for and the check that failed. */
function NowCard({ loop, now, onAbort, onResume, onReview, onDeploy }: { loop: LoopDetail; now: number } & LoopActions) {
	const n = loopNow(loop, now);
	const [rejecting, setRejecting] = useState(false);
	const [note, setNote] = useState("");
	const has = (action: string) => n.actions.some((x) => x === action);
	return (
		<section className={`loop-now tone-${n.tone}`} aria-label="现在">
			<div className="loop-now-head">
				<span className="loop-now-label">现在</span>
				<Pill tone={n.tone}>{n.pill}</Pill>
			</div>
			<p className="loop-now-text">{n.text}</p>
			{n.failed && <FailedCheck result={n.failed} />}
			{n.hint && <p className="hint">{n.hint}</p>}
			{has("review") && rejecting && (
				<textarea value={note} rows={3} placeholder="拒绝原因" aria-label="拒绝原因" onChange={(e) => setNote(e.target.value)} />
			)}
			{n.actions.length > 0 && (
				<div className="row">
					{has("review") &&
						(rejecting ? (
							<>
								<button onClick={() => onReview({ approve: false, note })} disabled={!note.trim()}>
									拒绝并继续
								</button>
								<button className="ghost" onClick={() => setRejecting(false)}>
									取消
								</button>
							</>
						) : (
							<>
								<button onClick={() => onReview({ approve: true })}>通过</button>
								<button className="ghost" onClick={() => setRejecting(true)}>
									拒绝…
								</button>
							</>
						))}
					{has("deploy") && <button onClick={onDeploy}>批准部署 ›</button>}
					{has("resume") && <button onClick={onResume}>继续</button>}
					{has("abort") && !rejecting && (
						<button className="ghost" onClick={onAbort}>
							中止
						</button>
					)}
				</div>
			)}
		</section>
	);
}

/** A failed check and its output; folded when the same check already shows above. */
function FailedCheck({ result, folded = false }: { result: AcceptanceResult; folded?: boolean }) {
	const head = (
		<>
			<code>{result.run ?? "人工确认"}</code> 未通过{result.exitCode !== null ? ` · exit ${result.exitCode}` : ""}
			{result.error ? ` · ${result.error}` : ""}
		</>
	);
	const output = <pre className="loop-output">{result.output || "（无输出）"}</pre>;
	if (folded) {
		return (
			<details className="loop-failed">
				<summary>{head}</summary>
				{output}
			</details>
		);
	}
	return (
		<div className="loop-failed">
			<p className="loop-failed-head">{head}</p>
			{output}
		</div>
	);
}

/** What the ledger recorded for an iteration: its task or its slice's task outcomes, why its check failed and what comes next. */
function EntryView({ entry, tasks = [] }: { entry: ProgressEntry; tasks?: Task[] }) {
	const committed = entry.committed && <span className="hint"> · 已提交 {entry.committed.slice(0, 7)}</span>;
	return (
		<div className="loop-cause">
			{entry.task && (
				<p>
					<b>任务</b> <code>{entry.task.id}</code> {entry.task.title}
					{committed}
				</p>
			)}
			{entry.outcomes && (
				<>
					<p>
						<b>切片</b> {entry.sliceReason ?? <span className="hint">未说明</span>}
						{committed}
					</p>
					<ul className="loop-outcomes">
						{entry.outcomes.map((o, i) => (
							<li key={i} data-task={o.id}>
								<p>
									<code>{o.id}</code> {tasks.find((t) => t.id === o.id)?.title}{" "}
									<Pill tone={o.status ? OUTCOME_TONE[o.status] : "err"}>{o.status ? OUTCOME_STATUS_LABEL[o.status] : "状态无效"}</Pill>
								</p>
								{o.evidence.length > 0 && <pre className="loop-output">{o.evidence.join("\n")}</pre>}
								{o.next && (
									<p>
										<b>下一步</b> {o.next}
									</p>
								)}
							</li>
						))}
					</ul>
				</>
			)}
			{entry.cause && (
				<>
					<p>
						<b>原因</b> {entry.cause.cause}
					</p>
					{entry.cause.evidence.length > 0 && <pre className="loop-output">{entry.cause.evidence.join("\n")}</pre>}
					{entry.cause.try && (
						<p>
							<b>下一步</b> {entry.cause.try}
						</p>
					)}
					{entry.cause.ignore && (
						<p className="hint">
							<b>忽略</b> {entry.cause.ignore}
						</p>
					)}
				</>
			)}
			{entry.next && !entry.cause && (
				<p>
					<b>下一步</b> {entry.next}
				</p>
			)}
			{entry.notes?.map((n, i) => (
				<p key={i} className="hint">
					{n}
				</p>
			))}
		</div>
	);
}

const eventKey = (e: TimelineEvent) => (e.kind === "ship" ? e.ship.id : e.iteration.id);

/** A loop's ship attempts and iterations newest first, the newest open, and its start with the goal at the end. */
function Timeline({ loop, now, shown, onOpen }: { loop: LoopDetail; now: number; shown?: AcceptanceResult; onOpen: (page: SubPage) => void }) {
	const events = timeline(loop);
	const [open, setOpen] = useState<Record<string, boolean>>({});
	const newest = events[0] ? eventKey(events[0]) : "start";
	const isOpen = (key: string) => open[key] ?? key === newest;
	const toggle = (key: string) => setOpen((o) => ({ ...o, [key]: !isOpen(key) }));
	return (
		<section className="loop-section">
			<h4>经过</h4>
			<ol className="loop-timeline">
				{events.map((e) => {
					const key = eventKey(e);
					return e.kind === "ship" ? (
						<ShipEvent key={key} ship={e.ship} number={e.number} now={now} shown={shown} open={isOpen(key)} onToggle={() => toggle(key)} onOpen={onOpen} />
					) : (
						<IterationEvent key={key} loop={loop} it={e.iteration} now={now} shown={shown} open={isOpen(key)} onToggle={() => toggle(key)} onOpen={onOpen} />
					);
				})}
				<li className="loop-event">
					<button className="loop-event-head" aria-expanded={isOpen("start")} onClick={() => toggle("start")}>
						<span className="loop-event-name">开始</span>
						<span className="loop-event-sub">
							{clock(loop.createdAt, now)} · 最多 {loop.maxIterations} {loop.oneShot ? "次运行" : "轮"}
						</span>
					</button>
					{isOpen("start") && (
						<div className="loop-event-body">
							<p className="loop-report">{loop.goal}</p>
							<ul className="loop-checks">
								{loop.acceptance.map((c, i) => (
									<li key={i}>{c.type === "command" ? <code>{c.run}</code> : "人工确认"}</li>
								))}
							</ul>
						</div>
					)}
				</li>
			</ol>
		</section>
	);
}

interface EventProps {
	now: number;
	shown?: AcceptanceResult;
	open: boolean;
	onToggle: () => void;
	onOpen: (page: SubPage) => void;
}

function EventHead({ name, tone, label, sub, why, open, onToggle }: { name: string; tone: Tone; label: string; sub: string; why: string | null; open: boolean; onToggle: () => void }) {
	return (
		<button className="loop-event-head" aria-expanded={open} onClick={onToggle}>
			<span className="loop-event-name">{name}</span>
			<Pill tone={tone}>{label}</Pill>
			<span className={`loop-event-sub${why && !open ? " tone-err" : ""}`}>
				{sub}
				{why && !open ? ` · ${why}` : ""}
			</span>
		</button>
	);
}

function IterationEvent({ loop, it, now, shown, open, onToggle, onOpen }: EventProps & { loop: LoopDetail; it: LoopIteration }) {
	const name = runName(loop.oneShot, it.index);
	const failed = it.acceptance.find((r) => !r.passed);
	const entry = loop.ledger?.progress.find((e) => e.index === it.index);
	const files = it.diff ? splitDiff(it.diff).length : 0;
	const why = failed ? `${failed.run ?? "人工确认"} 未通过` : it.error ?? (it.reviewNote ? `已拒绝：${it.reviewNote}` : null);
	return (
		<li className={`loop-event tone-${ITERATION_TONE[it.status]}`}>
			<EventHead
				name={name}
				tone={ITERATION_TONE[it.status]}
				label={ITERATION_STATUS_LABEL[it.status]}
				sub={`${clock(it.startedAt, now)} · ${took(Math.round(((it.finishedAt ?? now) - it.startedAt) / 1000))}`}
				why={why}
				open={open}
				onToggle={onToggle}
			/>
			{open && (
				<div className="loop-event-body">
					{it.error && <p className="loop-why">{it.error}</p>}
					{failed && <FailedCheck result={failed} folded={failed === shown} />}
					{entry && (entry.cause || entry.next || entry.outcomes) && <EntryView entry={entry} tasks={loop.ledger?.tasks} />}
					{it.reviewNote && (
						<p className="loop-cause">
							<b>审核意见</b> {it.reviewNote}
						</p>
					)}
					{it.status === "running" && <RunTrace loopId={loop.id} iterationId={it.id} running />}
					{it.summary && <p className="loop-report">{it.summary}</p>}
					{!failed && it.acceptance.length > 0 && (
						<p className="hint">
							{it.acceptance.length} 项验收通过{files ? ` · ${files} 个文件` : ""}
						</p>
					)}
					<button className="loop-more" aria-label={`${name}的全部细节`} onClick={() => onOpen({ page: "iteration", id: it.id })}>
						全部细节 ›
					</button>
				</div>
			)}
		</li>
	);
}

const STOPPED = ["failed", "refused", "interrupted"];

function ShipEvent({ ship, number, now, shown, open, onToggle, onOpen }: EventProps & { ship: ShipAttempt; number: number }) {
	const name = `发布 #${number}`;
	const failed = ship.checks.find((r) => !r.passed);
	const stopped = STOPPED.includes(ship.status);
	const spent = took(Math.round(((ship.status === "running" ? now : ship.updatedAt) - ship.createdAt) / 1000));
	return (
		<li className={`loop-event tone-${SHIP_TONE[ship.status]}`}>
			<EventHead
				name={name}
				tone={SHIP_TONE[ship.status]}
				label={SHIP_STATUS_LABEL[ship.status]}
				sub={`${clock(ship.createdAt, now)} · ${spent}`}
				why={stopped ? `停止于「${SHIP_STAGE_LABEL[ship.stage]}」` : null}
				open={open}
				onToggle={onToggle}
			/>
			{open && (
				<div className="loop-event-body">
					{ship.reason && <p className="loop-why">{ship.reason}</p>}
					{failed && <FailedCheck result={failed} folded={failed === shown} />}
					{ship.status === "running" && <p className="hint">正在{SHIP_STAGE_LABEL[ship.stage]}…</p>}
					{!stopped && ship.status !== "running" && (
						<p className="hint">
							{ship.stages.length} 个阶段通过 · 共 {spent}
							{ship.pushedSha && (
								<>
									 · 已推送 <code>{ship.pushedSha.slice(0, 8)}</code>
								</>
							)}
						</p>
					)}
					<button className="loop-more" aria-label={`${name}的全部细节`} onClick={() => onOpen({ page: "ship", id: ship.id })}>
						全部细节 ›
					</button>
				</div>
			)}
		</li>
	);
}

function AcceptanceView({ result, open }: { result: AcceptanceResult; open: boolean }) {
	return (
		<details className="acceptance" open={open}>
			<summary>
				<code>{result.run ?? result.type}</code> {result.passed ? "✓" : `✗ exit ${result.exitCode ?? "none"}`}
				{result.error && ` (${result.error})`} <span className="hint">{took(Math.round(result.durationMs / 1000))}</span>
			</summary>
			<pre className="loop-output">{result.output || "（无输出）"}</pre>
		</details>
	);
}

/** An iteration's details: why it failed, what it reported, every check, what its worker did, and the rows to its changes and conversation. */
function IterationPage({ loop, it, now, onOpen, onChat }: { loop: LoopDetail; it: LoopIteration; now: number; onOpen: (page: SubPage) => void; onChat: (it: LoopIteration) => void }) {
	const failed = it.acceptance.find((r) => !r.passed);
	const entry = loop.ledger?.progress.find((e) => e.index === it.index);
	const files = it.diff ? splitDiff(it.diff) : [];
	const usage = usageLine([it.usage]);
	return (
		<div className="loop-view loop-page">
			<header className="loop-head">
				<h3>{runName(loop.oneShot, it.index)}</h3>
				<p className="loop-meta">
					<Pill tone={ITERATION_TONE[it.status]}>{ITERATION_STATUS_LABEL[it.status]}</Pill> {clock(it.startedAt, now)} · {took(Math.round(((it.finishedAt ?? now) - it.startedAt) / 1000))}
				</p>
			</header>
			{it.error && <p className="banner error">{it.error}</p>}
			{(failed || entry) && (
				<section className="loop-section">
					<h4>{failed ? "失败原因" : "记录"}</h4>
					{failed && <FailedCheck result={failed} />}
					{entry && <EntryView entry={entry} tasks={loop.ledger?.tasks} />}
				</section>
			)}
			{it.reviewNote && (
				<section className="loop-section">
					<h4>审核意见</h4>
					<p className="loop-report">{it.reviewNote}</p>
				</section>
			)}
			{it.summary && (
				<section className="loop-section">
					<h4>{loop.oneShot ? "报告" : "交接"}</h4>
					<p className="loop-report">{it.summary}</p>
				</section>
			)}
			{it.acceptance.length > 0 && (
				<section className="loop-section">
					<h4>验收</h4>
					{it.acceptance.map((r, i) => (
						<AcceptanceView key={i} result={r} open={false} />
					))}
				</section>
			)}
			<RunTrace loopId={loop.id} iterationId={it.id} running={it.status === "running"} />
			{(files.length > 0 || it.sessionId) && (
				<div className="loop-links">
					{files.length > 0 && (
						<button className="loop-link" onClick={() => onOpen({ page: "changes", id: it.id })}>
							<span>改动</span>
							<span className="loop-link-value">{files.length} 个文件</span>
							<span className="loop-chev">›</span>
						</button>
					)}
					{it.sessionId && (
						<button className="loop-link" onClick={() => onChat(it)}>
							<span>查看对话</span>
							<span className="loop-link-value" />
							<span className="loop-chev">›</span>
						</button>
					)}
				</div>
			)}
			{usage && <p className="hint">估算花费 {usage}</p>}
		</div>
	);
}

/** A ship attempt's details: why it stopped, its stages and how long each took, its deletion review, its checks and its commits. */
function ShipPage({ ship, number, now, onDeploy }: { ship: ShipAttempt; number: number; now: number; onDeploy: () => void }) {
	const failed = ship.checks.find((r) => !r.passed);
	return (
		<div className="loop-view loop-page">
			<header className="loop-head">
				<h3>发布 #{number}</h3>
				<p className="loop-meta">
					<Pill tone={SHIP_TONE[ship.status]}>{SHIP_STATUS_LABEL[ship.status]}</Pill> {clock(ship.createdAt, now)}
				</p>
			</header>
			{ship.reason && <p className="banner error">{ship.reason}</p>}
			{ship.status === "pushed" && (
				<div className="row">
					<button onClick={onDeploy}>批准部署 ›</button>
				</div>
			)}
			<section className="loop-section">
				<h4>阶段</h4>
				<ol className="loop-stages">
					{stageSpans(ship, now).map((s, i) => (
						<li key={i}>
							<span>
								{SHIP_STAGE_LABEL[s.stage]}
								{s.note && (
									<>
										{" "}
										<code>{s.note}</code>
									</>
								)}
							</span>
							<span className="hint">{took(Math.round(s.ms / 1000))}</span>
						</li>
					))}
				</ol>
			</section>
			{ship.review.length > 0 && (
				<section className="loop-section">
					<h4>删除审查</h4>
					<ul className="loop-checks ship-review">
						{ship.review.map((d) => (
							<li key={d.key} className={d.reason ? undefined : "missing"}>
								<code>{d.key}</code>
								{d.tests && d.tests.length > 0 && <span className="hint"> 连同测试 {d.tests.join("、")}</span>}：{d.reason ?? "未说明理由"}
							</li>
						))}
					</ul>
				</section>
			)}
			{ship.checks.length > 0 && (
				<section className="loop-section">
					<h4>检查</h4>
					{ship.checks.map((r, i) => (
						<AcceptanceView key={i} result={r} open={r === failed} />
					))}
				</section>
			)}
			{ship.baseSha && (
				<section className="loop-section">
					<h4>提交</h4>
					<dl className="loop-info">
						<div>
							<dt>基于 main</dt>
							<dd>
								<code>{ship.baseSha.slice(0, 8)}</code>
							</dd>
						</div>
						{ship.candidateSha && (
							<div>
								<dt>合成提交</dt>
								<dd>
									<code>{ship.candidateSha.slice(0, 8)}</code>
								</dd>
							</div>
						)}
						{ship.pushedSha && (
							<div>
								<dt>推送</dt>
								<dd>
									<code>{ship.pushedSha}</code>
								</dd>
							</div>
						)}
						{ship.requestedAt && (
							<div>
								<dt>请求部署</dt>
								<dd>{clock(ship.requestedAt, now)}</dd>
							</div>
						)}
					</dl>
				</section>
			)}
		</div>
	);
}

/** An iteration's changes, a fold per file. */
function ChangesPage({ loop, it }: { loop: LoopDetail; it: LoopIteration }) {
	const files = splitDiff(it.diff ?? "");
	return (
		<div className="loop-view loop-page">
			<header className="loop-head">
				<h3>改动</h3>
				<p className="loop-meta">
					{runName(loop.oneShot, it.index)} · {files.length} 个文件
				</p>
			</header>
			{files.map((f) => (
				<details key={f.path} className="acceptance loop-file">
					<summary>
						<code>{f.path}</code>{" "}
						<span className="hint">
							<span className="tone-ok">+{f.added}</span> <span className="tone-err">−{f.removed}</span>
						</span>
					</summary>
					<pre className="loop-output loop-diff">
						{f.text.split("\n").map((line, i) => (
							<span key={i} className={line.startsWith("+") ? "tone-ok" : line.startsWith("-") ? "tone-err" : line.startsWith("@@") || line.startsWith("diff ") ? "hint" : undefined}>
								{line}
								{"\n"}
							</span>
						))}
					</pre>
				</details>
			))}
		</div>
	);
}

/** Who and what runs the loop, where, since when, and what it has cost. */
function InfoPage({ loop }: { loop: LoopDetail }) {
	const model = loop.model ?? [...loop.iterations].reverse().find((it) => it.usage?.model)?.usage?.model ?? null;
	const rows: Array<[string, ReactNode]> = [
		["agent", loop.profile],
		["写代码", loop.worker],
		["方式", loop.oneShot ? `one-shot，最多 ${loop.maxIterations} 次运行` : `loop，最多 ${loop.maxIterations} 轮`],
		["模型", model],
		["力度", loop.effort],
		["改动", loop.change && <code>{loop.change}</code>],
		["分支", loop.branch && <code>{loop.branch}</code>],
		["工作区", loop.workspace && <code>{loop.workspace}</code>],
		["开始", new Date(loop.createdAt).toLocaleString()],
		["估算花费", usageLine(loop.iterations.map((it) => it.usage))],
		["结束原因", loop.reason],
	];
	return (
		<div className="loop-view loop-page">
			<header className="loop-head">
				<h3>信息与花费</h3>
			</header>
			<dl className="loop-info">
				{rows
					.filter(([, v]) => v)
					.map(([k, v]) => (
						<div key={k}>
							<dt>{k}</dt>
							<dd>{v}</dd>
						</div>
					))}
			</dl>
		</div>
	);
}

/** The task list the loop's planning iteration wrote, with the ones that pass. */
function TasksPage({ loop }: { loop: LoopDetail }) {
	const tasks = loop.ledger?.tasks ?? [];
	return (
		<div className="loop-view loop-page">
			<header className="loop-head">
				<h3>任务清单</h3>
				<p className="loop-meta">
					{tasks.filter((t) => t.passes).length}/{tasks.length} 完成
				</p>
			</header>
			<ul className="loop-checks loop-tasks">
				{tasks.map((t) => (
					<li key={t.id} className={t.passes ? "done" : ""}>
						{t.passes ? "✓" : "✗"} <code>{t.id}</code> {t.title}
						{t.check && <span className="hint"> · {t.check}</span>}
					</li>
				))}
			</ul>
		</div>
	);
}
